import React, { Component, ErrorInfo, PropsWithChildren } from 'react'

import { handleError, logError } from '@helper/errors'
import { AppError } from '@helper/errors/AppError'
import { PropsWithClassName } from '@helper/PropsWithClassName'

import ErrorFallback from './ErrorFallback'

interface Props extends PropsWithClassName {
  fallback?: React.ComponentType<ErrorFallbackProps>
  onError?: (error: AppError, errorInfo: ErrorInfo) => void
}

interface ErrorFallbackProps {
  error: AppError
  onRetry: () => void
}

interface State {
  hasError: boolean
  error: AppError | null
}

class ErrorBoundary extends Component<PropsWithChildren<Props>, State> {
  constructor(props: PropsWithChildren<Props>) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
    }
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error: handleError(error),
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const appError = handleError(error)
    logError(appError, {
      componentStack: errorInfo.componentStack,
    })

    if (this.props.onError) {
      this.props.onError(appError, errorInfo)
    }
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
    })
  }

  render(): React.ReactNode {
    if (this.state.hasError && this.state.error) {
      const FallbackComponent = this.props.fallback || ErrorFallback
      try {
        return (
          <FallbackComponent
            error={this.state.error}
            onRetry={this.handleReset}
          />
        )
      } catch (fallbackError) {
        // If the fallback itself throws, show a minimal error message
        logError(handleError(fallbackError), {
          context: 'ErrorBoundary fallback failed',
        })
        return (
          <div className="min-h-screen flex items-center justify-center p-8">
            <div className="text-center">
              <h1 className="text-2xl font-bold mb-4">
                Ein Fehler ist aufgetreten
              </h1>
              <p className="mb-4">Die Anwendung konnte nicht geladen werden.</p>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Seite neu laden
              </button>
            </div>
          </div>
        )
      }
    }

    return this.props.children
  }
}

export { ErrorBoundary }
export type { ErrorFallbackProps }
